import { useState, type FC, type FormEvent } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { Crosshair, Loader2, Sparkles, Copy, Check } from 'lucide-react';

const PERSONAS = [
  { value: '', label: 'Non spécifié' },
  { value: 'cto', label: 'CTO' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'ceo_startup', label: 'CEO Startup' },
  { value: 'qa_lead', label: 'QA Lead' },
  { value: 'vp_product', label: 'VP Product' },
] as const;

interface OutreachResult {
  companyName: string;
  subjectLine?: string;
  messageBody?: string;
  followUpSuggestion?: string;
  personalizationNotes?: string[];
}

/** Strip Markdown bold prefix "**Objet :**" from subject line */
const cleanSubject = (raw: string) => raw.replace(/^\*\*Objet\s?:\*\*\s?/i, '').trim();

/** Remove first line from body if it duplicates the subject line */
const cleanBody = (raw: string) => {
  const lines = raw.split('\n');
  if (lines[0]?.match(/^\*?\*?Objet/i)) lines.shift();
  return lines.join('\n').trim();
};

interface ColdOutreachGeneratorProps {
  onGenerated: () => void;
}

const CopyBtn: FC<{ text: string; label?: string }> = ({ text, label = 'Copier' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copié !' : label}
    </button>
  );
};

const ColdOutreachGenerator: FC<ColdOutreachGeneratorProps> = ({ onGenerated }) => {
  const [companyName, setCompanyName] = useState('');
  const [sector, setSector] = useState('');
  const [persona, setPersona] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [result, setResult] = useState<OutreachResult | null>(null);

  const { mutate, isLoading } = useCustomMutation();

  const isValid = companyName.trim().length >= 2 && sector.trim().length >= 2;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;
    mutate(
      {
        url: '/api/v1/admin/growth/sales/cold-outreach',
        method: 'post',
        values: {
          companyName: companyName.trim(),
          sector: sector.trim(),
          persona: persona || undefined,
          additionalContext: additionalContext.trim() || undefined,
        },
      },
      {
        onSuccess: (response) => {
          const d = response?.data?.data ?? response?.data ?? {};
          setResult(d as OutreachResult);
          onGenerated();
        },
      },
    );
  };

  const inputClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

  const selectClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

  const textareaClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none resize-none transition-colors';

  return (
    <div className="space-y-5">
      {/* Form */}
      <div className="fc-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Crosshair className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Cold Outreach Generator</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                Entreprise <span className="text-red-500">*</span>
              </label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Ex: Doctolib, BlaBlaCar..." className={inputClasses} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                Secteur <span className="text-red-500">*</span>
              </label>
              <input type="text" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="Ex: HealthTech, Mobilité..." className={inputClasses} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Persona</label>
              <select value={persona} onChange={(e) => setPersona(e.target.value)} className={selectClasses}>
                {PERSONAS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                Contexte <span className="text-slate-400 dark:text-slate-500">(optionnel)</span>
              </label>
              <textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Informations spécifiques, levée de fonds, actualité..."
                rows={2}
                className={textareaClasses}
              />
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button type="submit" disabled={!isValid || isLoading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {isLoading ? 'Génération...' : 'Générer Outreach'}
            </button>
          </div>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="fc-card p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Outreach — {result.companyName}</h3>

          {/* Subject Line */}
          {result.subjectLine && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Objet :</span>
              <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-sm font-medium text-blue-700 dark:text-blue-300">
                {cleanSubject(result.subjectLine)}
              </span>
              <CopyBtn text={cleanSubject(result.subjectLine)} label="Copier objet" />
            </div>
          )}

          {/* Message Body */}
          {result.messageBody && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Corps du message</p>
                <CopyBtn text={cleanBody(result.messageBody)} label="Copier body" />
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
                <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed">{cleanBody(result.messageBody)}</p>
              </div>
            </div>
          )}

          {/* Follow-up */}
          {result.followUpSuggestion && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Suggestion de relance</p>
              <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">{result.followUpSuggestion}</p>
              </div>
            </div>
          )}

          {/* Personalization Notes */}
          {result.personalizationNotes && result.personalizationNotes.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Notes de personnalisation</p>
              <ul className="space-y-1.5">
                {result.personalizationNotes.map((note, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColdOutreachGenerator;
